import React, { createContext, useContext, useState, useEffect } from "react";

const SquareContext = createContext();

export const SquareProvider = ({ children }) => {
  const [payments, setPayments] = useState(null);
  const [card, setCard] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const appId = import.meta.env.VITE_SQUARE_APP_ID;
    const locationId = import.meta.env.VITE_SQUARE_LOCATION_ID;

    if (!appId || !locationId) {
      console.error("Missing Square credentials");
      return;
    }

    async function initializeSquare() {
      try {
        if (!window.Square) {
          throw new Error("Square.js failed to load properly");
        }

        const paymentsInstance = await window.Square.payments(appId, locationId);
        console.log("Square payments initialized with:", { appId, locationId });
        
        if (mounted) {
          setPayments(paymentsInstance);
          setReady(true);
        }
      } catch (err) {
        console.error("Error initializing Square payments:", err);
        if (mounted) {
          setError(err);
          setReady(false);
        }
      }
    }

    // Load Square.js sandbox script
    const script = document.createElement("script");
    script.src = "https://sandbox.web.squarecdn.com/v1/square.js";
    script.crossOrigin = "anonymous";
    script.onload = () => {
      console.log("Square.js loaded successfully");
      initializeSquare();
    };
    script.onerror = (e) => {
      console.error("Failed to load Square.js:", e);
      setError(new Error("Failed to load Square.js"));
    };
    document.head.appendChild(script);

    return () => {
      mounted = false;
      if (card) {
        card.destroy();
      }
    };
  }, []);

  const createPayment = async (token) => {
    try {
      const response = await fetch("https://dorze-square-worker.httpsindex-90qpagesdev.workers.dev/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sourceId: token,
          locationId: import.meta.env.VITE_SQUARE_LOCATION_ID,
        }),
      });

      if (!response.ok) {
        throw new Error("Payment failed");
      }

      return await response.json();
    } catch (err) {
      console.error("Payment error:", err);
      throw err;
    }
  };

  return (
    <SquareContext.Provider value={{ payments, card, setCard, ready, error, createPayment }}>
      {children}
    </SquareContext.Provider>
  );
};

export const useSquare = () => {
  const context = useContext(SquareContext);
  if (!context) {
    throw new Error("useSquare must be used within a SquareProvider");
  }
  return context;
};
